import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import ProjectPaymentActivationApi from "../../../../../infrastructure/api/projectPaymentActivation";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const formatPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("fa-IR");
};

const statusColor = (statusTitle) => {
  const text = String(statusTitle || "");
  if (text.includes("فعال") && !text.includes("غیر")) return "success";
  if (text.includes("معلق")) return "warning";
  if (text.includes("لغو")) return "error";
  if (text.includes("تکمیل")) return "info";
  if (text.includes("انتظار")) return "default";
  return "default";
};

const ProjectPaymentActivationPage = ({ source = "Management" }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState("8211");
  const [milestones, setMilestones] = useState([]);
  const [activations, setActivations] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [notes, setNotes] = useState("");
  const [plannedAmount, setPlannedAmount] = useState("");

  const areaLabel = source === "Management" ? "مدیریت" : "اپراتور سولارنت";
  const basePath =
    source === "Management" ? "/SLN/Management" : "/SLN/SolarentOperator";

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    const rid = requestId.trim() ? Number(requestId.trim()) : null;
    const [msRes, actRes] = await Promise.all([
      ProjectPaymentActivationApi.getPaymentTriggerMilestones(source, rid),
      rid
        ? ProjectPaymentActivationApi.getPaymentActivations(source, { requestId: rid })
        : ProjectPaymentActivationApi.getPaymentActivations(source, {}),
    ]);
    if (!msRes.ok) {
      setError(msRes.message || "خطا در دریافت مایلستون‌های محرک پرداخت");
      setMilestones([]);
    } else {
      setMilestones(Array.isArray(msRes.data) ? msRes.data : []);
    }
    if (!actRes.ok && !msRes.ok) {
      setActivations([]);
    } else if (!actRes.ok) {
      setError((prev) => prev || actRes.message || "خطا در دریافت فعال‌سازی‌های پرداخت");
      setActivations([]);
    } else {
      setActivations(Array.isArray(actRes.data) ? actRes.data : []);
    }
    setLoading(false);
  }, [requestId, source]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (row) => {
    if (!row?.milestoneId || !row.canCreatePaymentActivation) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const amount = plannedAmount.trim() ? Number(plannedAmount.trim()) : undefined;
    const res = await ProjectPaymentActivationApi.createPaymentActivation(source, {
      projectExecutionPlanMilestoneId: row.milestoneId,
      paymentType: row.suggestedPaymentType,
      plannedAmount: Number.isFinite(amount) ? amount : undefined,
      notes: notes.trim() || undefined,
    });
    if (!res.ok) {
      setError(res.message || "ایجاد فعال‌سازی پرداخت ناموفق بود.");
    } else {
      setSuccess(res.message || "فعال‌سازی پرداخت ایجاد شد.");
      setNotes("");
      setPlannedAmount("");
      await loadData();
    }
    setSubmitting(false);
  };

  const runAction = async (action, item) => {
    if (!item?.id) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    const payload = {
      projectPaymentActivationId: item.id,
      notes: notes.trim() || undefined,
    };
    let res;
    if (action === "approve") res = await ProjectPaymentActivationApi.approvePaymentActivation(source, payload);
    else if (action === "suspend") res = await ProjectPaymentActivationApi.suspendPaymentActivation(source, payload);
    else if (action === "cancel") res = await ProjectPaymentActivationApi.cancelPaymentActivation(source, payload);
    else if (action === "complete") res = await ProjectPaymentActivationApi.completePaymentActivation(source, payload);
    if (!res?.ok) {
      setError(res?.message || "عملیات ناموفق بود.");
    } else {
      setSuccess(res.message || "عملیات انجام شد.");
      setNotes("");
      await loadData();
    }
    setSubmitting(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <BreadcrumbsComponent
        items={[
          { title: areaLabel, href: basePath },
          { title: "فعال‌سازی پرداخت‌ها" },
        ]}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, mb: 2 }}>
        <Typography variant="h5">فعال‌سازی پرداخت‌ها</Typography>
        <IconButton onClick={loadData} disabled={loading || submitting} aria-label="بارگذاری مجدد">
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="شناسه درخواست"
                value={requestId}
                onChange={(e) => setRequestId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="مبلغ برنامه‌ریزی (اختیاری)"
                value={plannedAmount}
                onChange={(e) => setPlannedAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="یادداشت عملیات"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={loadData} disabled={loading}>
                اعمال فیلتر
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardHeader title="مایلستون‌های محرک پرداخت" subheader="فقط برنامه تایید شده / در حال اجرا" />
        <CardContent>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>مایلستون</TableCell>
                  <TableCell>نوع پرداخت پیشنهادی</TableCell>
                  <TableCell>حالت تامین مالی</TableCell>
                  <TableCell>وضعیت برنامه</TableCell>
                  <TableCell align="center">عملیات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {milestones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {loading ? "در حال بارگذاری..." : "مایلستونی یافت نشد."}
                    </TableCell>
                  </TableRow>
                ) : (
                  milestones.map((row) => (
                    <TableRow key={row.milestoneId}>
                      <TableCell>
                        <Typography variant="body2">{row.milestoneTitle}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{row.milestoneId} — درخواست {row.requestReferenceId || row.requestId}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.suggestedPaymentTypeTitle}</TableCell>
                      <TableCell>{row.financingModeTitle}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.planStatusTitle} />
                      </TableCell>
                      <TableCell align="center">
                        {row.canCreatePaymentActivation ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PlayCircleOutlineIcon />}
                            disabled={submitting}
                            onClick={() => handleCreate(row)}
                          >
                            فعال‌سازی
                          </Button>
                        ) : (
                          <Chip
                            size="small"
                            color="info"
                            label={`فعال #${row.existingPaymentActivationId}`}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="فعال‌سازی‌های پرداخت ثبت‌شده" />
        <CardContent>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>مایلستون</TableCell>
                  <TableCell>نوع / وضعیت</TableCell>
                  <TableCell>مبلغ</TableCell>
                  <TableCell>تامین مالی</TableCell>
                  <TableCell>تاریخ</TableCell>
                  <TableCell align="center">عملیات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {loading ? "در حال بارگذاری..." : "فعال‌سازی پرداختی ثبت نشده."}
                    </TableCell>
                  </TableRow>
                ) : (
                  activations.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2">{item.milestoneTitle}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{item.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Chip size="small" label={item.paymentTypeTitle} variant="outlined" />
                          <Chip
                            size="small"
                            color={statusColor(item.paymentActivationStatusTitle)}
                            label={item.paymentActivationStatusTitle}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          برنامه: {formatPrice(item.plannedAmount)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          تایید: {formatPrice(item.approvedAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.financingModeTitle}</TableCell>
                      <TableCell>
                        {item.activatedAt ? toJalaliFullDate(item.activatedAt) : "-"}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                          {item.canApprove && (
                            <Button
                              size="small"
                              color="success"
                              startIcon={<CheckCircleOutlineIcon />}
                              disabled={submitting}
                              onClick={() => runAction("approve", item)}
                            >
                              تایید
                            </Button>
                          )}
                          {item.canSuspend && (
                            <Button
                              size="small"
                              color="warning"
                              startIcon={<PauseCircleOutlineIcon />}
                              disabled={submitting}
                              onClick={() => runAction("suspend", item)}
                            >
                              تعلیق
                            </Button>
                          )}
                          {item.canComplete && (
                            <Button
                              size="small"
                              color="primary"
                              startIcon={<DoneAllIcon />}
                              disabled={submitting}
                              onClick={() => runAction("complete", item)}
                            >
                              تکمیل
                            </Button>
                          )}
                          {item.canCancel && (
                            <Button
                              size="small"
                              color="error"
                              disabled={submitting}
                              onClick={() => runAction("cancel", item)}
                            >
                              لغو
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProjectPaymentActivationPage;
